import { useState, useEffect, useRef } from "react";
import { MapPin, Loader2, Plane, Home, Building2, Coffee } from "lucide-react";
import MapModal from "./ModalMap";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { googleMapsApiKey } from "../config";
import { useLoadScript } from "@react-google-maps/api";
import { popularLocations } from "../utils/popularLocations";

const placesLibrary = ["places"];

// Mapping tipe ke ikon lucide
const typeIcons = {
  Airport: Plane,
  Hotel: Home,
  Office: Building2,
  Restaurant: Coffee,
  Default: MapPin,
};

function InputPlace({ onPlaceChanged, value, onChange, error, ...props }) {
  const [locations, setLocations] = useState({ data: popularLocations });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isLocationPermissionGranted, setIsLocationPermissionGranted] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const { t } = useTranslation();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey,
    libraries: placesLibrary,
  });

  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        setIsLocationPermissionGranted(result.state === "granted");
      });
    }

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!value) {
      setLocations({ data: popularLocations });
      return;
    }

    const timeout = setTimeout(() => {
      const fetchLocations = async () => {
        setIsLoading(true);
        try {
          const res = await api.get(`/search-places?query=${encodeURIComponent(value)}`);
          setLocations(res.data || []);
        } catch (error) {
          console.error("Gagal fetch locations:", error);
          setLocations([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchLocations();
    }, 300);

    return () => clearTimeout(timeout);
  }, [value]);

  const handleInputChange = (e) => {
    onChange(e);
    setShowSuggestions(true);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (location) => {
    onPlaceChanged(location, location.name);
    setShowSuggestions(false);
    setIsFocused(false);
  };

  const handleSelectLocationFromMap = (place, name) => {
    onPlaceChanged(place, name);
  };

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <div className="relative">
        <input
          type="text"
          placeholder={t("type_or_choose_location")}
          value={value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          className={`w-full bg-white text-slate-700 text-base md:text-lg rounded-sm px-5 py-4 pr-20 border ${
            error ? "border-red-500 focus:ring-red-500" : "border-slate-300 focus:ring-[#f5272b]"
          } placeholder-slate-400 focus:outline-none focus:ring-2 transition-all`}
          {...props}
        />
        {isLocationPermissionGranted && (
          <button
            type="button"
            title={t("select_from_map")}
            onClick={() => setOpen(true)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#f5272b] transition-colors"
          >
            <MapPin size={22} />
          </button>
        )}
      </div>

      {showSuggestions && isFocused && (
        <div className="absolute z-50 w-full mt-2 overflow-y-auto bg-white border rounded-sm shadow-lg border-slate-200 max-h-80">
          {isLoading && (
            <div className="flex items-center justify-center py-5">
              <Loader2 className="mr-2 animate-spin" size={20} />
              <span className="text-sm text-slate-500">{t("loading")}</span>
            </div>
          )}
          {!isLoading && locations?.data?.length > 0 && (
            <>
              {!value ? (
                <div className="p-3 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-700">{t("popular_locations")}</h3>
                </div>
              ) : null}
              <div className="py-2">
                {locations?.data?.map((location, idx) => {
                  const IconComponent = location.icon || typeIcons[location.type] || typeIcons.Default;
                  return (
                    <button
                      key={location.place_id || idx}
                      type="button"
                      onClick={() => handleSuggestionClick(location)}
                      className="flex items-start w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 group"
                    >
                      <div className="flex-shrink-0 text-slate-500">
                        <IconComponent size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate text-slate-800 md:text-base">{location.name}</p>
                        <p className="text-xs md:text-sm text-slate-500 mt-0.5 truncate">
                          {location.formatted_address}
                        </p>
                        {location.type && (
                          <span className="mt-1 inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 shrink-0">
                            {location.type}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="p-3 border-t border-slate-100 bg-slate-50">
                <p className="text-xs text-center text-slate-600">{t("manual_input_hint")}</p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal Map */}
      <MapModal
        isLoaded={isLoaded}
        isOpen={open}
        onLocationSelect={handleSelectLocationFromMap}
        onClose={() => setOpen(false)}
      />

      {/* Error */}
      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
}

export default InputPlace;
